/**
 * <TutorLanguageList> — les langues étrangères d'un candidat, chacune avec son niveau (organism).
 *
 * L'ancien `LanguageSelector` : les langues qu'on connaît, puis le niveau de chacune
 * (« Conversationnel », « Courant », « Langue maternelle », et « Niveau requis » tant qu'il manque).
 * Ici, toutes les langues en puces de matière (`<SubjectBadge>`, leur picto et leur couleur — Wajih,
 * 03/11 : plus un menu déroulant sans picto) : toucher une langue la déclare ou la retire. Sous elles,
 * une carte-liste des langues déclarées, chacune avec ses trois niveaux en puces (`<TutorChoiceStep>`,
 * sa consigne et son « Niveau requis »).
 *
 * Contrôlée : les langues, leurs niveaux, l'erreur arrivent en props ; chaque geste remonte.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import {
    type UITutorLanguage,
    type UITutorLanguageLevel,
    type UITutorLanguageOption,
} from "../../../ports/primary/ui/types/tutorCandidature"
import { type UISubject } from "../../../ports/primary/ui/types"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { ImageIcon } from "../../atoms/icons/ImageIcon"
import { Text } from "../../atoms/typography/Text"
import { CardList, CardListRow } from "../../molecules/CardList"
import { TutorChoiceStep } from "../TutorChoiceStep"

const TUTOR_LANGUAGE_TEXTS = {
    levels: {
        conversational: "Conversationnel",
        fluent: "Courant",
        native: "Langue maternelle",
    } satisfies Record<UITutorLanguageLevel, string>,
    levelLabel: "Niveau de maîtrise",
    levelRequired: "Niveau requis",
}

/** La puce d'une langue : la matière du catalogue, ou une puce neutre quand il ne sert pas de picto. */
const languageSubject = (language: UITutorLanguageOption): UISubject => ({
    id: language.id,
    name: language.name,
    color: language.color ?? "subject-default",
    iconUrl: language.iconUrl,
})

const LEVELS: UITutorLanguageLevel[] = ["conversational", "fluent", "native"]
const LEVEL_OPTIONS = LEVELS.map((level) => ({ id: level, label: TUTOR_LANGUAGE_TEXTS.levels[level] }))

export type TutorLanguageListProps = {
    languages: UITutorLanguage[]
    /** Toutes les langues qu'on peut déclarer, avec leur picto quand le catalogue le sert. */
    available: UITutorLanguageOption[]
    onAdd: (id: string) => void
    onRemove: (id: string) => void
    onChangeLevel: (id: string, level: UITutorLanguageLevel) => void
    /** « Continuer » a été pressé avec un niveau manquant : la ligne le dit. */
    showErrors?: boolean
    testID?: string
}

export function TutorLanguageList(props: TutorLanguageListProps) {
    const { languages, available, onAdd, onRemove, onChangeLevel, showErrors = false, testID } = props
    const declared = (id: string) => languages.some((language) => language.id === id)
    const optionOf = (language: UITutorLanguage) =>
        available.find((option) => option.id === language.id) ?? { id: language.id, name: language.name }
    // Une langue déclarée hors du catalogue garde sa puce : c'est elle qui la retire.
    const offCatalogue = languages.filter((language) => !available.some((option) => option.id === language.id))
    const badges = [...available, ...offCatalogue.map(({ id, name }) => ({ id, name }))].map(languageSubject)

    return (
        <Stack gap="5" testID={testID}>
            <TutorChoiceStep
                subjects={badges}
                selectedIds={languages.map((language) => language.id)}
                onToggle={(id) => (declared(id) ? onRemove(id) : onAdd(id))}
            />
            {languages.length > 0 && (
                <CardList>
                    {languages.map((language) => (
                        <CardListRow key={language.id}>
                            <Stack gap="2">
                                <Row align="center" gap="2">
                                    <ImageIcon uri={optionOf(language).iconUrl} size={24} />
                                    <Text size="text-lg" weight="bold" color="text-primary" style={styles.name}>
                                        {language.name}
                                    </Text>
                                </Row>
                                <TutorChoiceStep
                                    description={TUTOR_LANGUAGE_TEXTS.levelLabel}
                                    options={LEVEL_OPTIONS}
                                    selectedIds={language.level != null ? [language.level] : []}
                                    onToggle={(id) => {
                                        const level = LEVELS.find((candidate) => candidate === id)
                                        if (level != null) onChangeLevel(language.id, level)
                                    }}
                                    error={
                                        showErrors && language.level == null ? TUTOR_LANGUAGE_TEXTS.levelRequired : null
                                    }
                                />
                            </Stack>
                        </CardListRow>
                    ))}
                </CardList>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    name: { flexShrink: 1 },
}))
